import type { App } from 'vue';
import { SSEClient } from '../../utils/sse';
import { buildApiUrl } from '../../utils/url';
import { BaseService } from '../base';

// SSE 初始化配置（用于应用初始化）
export interface SSEConfig {
  // SSE 基础 URL
  baseURL: string;
  // 路径（可选）
  path?: string;
  // 查询参数（可选）
  params?: Record<string, string | number | undefined>;
  // 是否自动连接（默认 false）
  autoConnect?: boolean;
  // 获取用户认证信息的函数（用于自动连接）
  getAuthInfo?: () => {
    userId?: number;
    key?: string;
    [key: string]: string | number | undefined;
  } | null;
  // 消息处理器（可选，也可以通过 subscribe 订阅）
  onMessage?: (msg: unknown) => void;
  // 错误处理器（可选）
  onError?: (error: Event) => void;
  // 连接打开处理器（可选）
  onOpen?: () => void;
  // 连接关闭处理器（可选）
  onClose?: () => void;
}

// SSE 连接配置（用于手动连接）
export interface SSEServiceConfig {
  // SSE 基础 URL
  sseUrl: string;
  // 路径（可选）
  path?: string;
  // 查询参数（可选）
  params?: Record<string, string | number | undefined>;
}

// 消息订阅回调类型
export type SSEMessageCallback = (msg: unknown) => void;

// 错误回调类型
export type SSEErrorCallback = (error: Event) => void;

// SSE 服务类
export class SSEService extends BaseService {
  private sse: SSEClient | null = null;
  private subscribers: SSEMessageCallback[] = [];
  private errorSubscribers: SSEErrorCallback[] = [];
  private initConfig: SSEConfig | null = null;

  constructor(initConfig?: SSEConfig) {
    super('SSEService');
    this.initConfig = initConfig || null;
  }

  // 初始化服务
  protected async onInitialize(_app?: App): Promise<void> {
    // SSR 环境下不初始化 SSE 连接
    if (import.meta.server) return;

    // 如果配置了消息处理器，自动订阅
    if (this.initConfig?.onMessage) {
      this.subscribe(this.initConfig.onMessage);
    }

    // 如果配置了错误处理器，自动订阅
    if (this.initConfig?.onError) {
      this.subscribeError(this.initConfig.onError);
    }

    // 如果配置了自动连接，首次尝试连接
    if (this.initConfig?.autoConnect) {
      this.autoReconnect();
    }
  }

  // 自动重连（根据 getAuthInfo 返回值决定连接或断开）
  public autoReconnect(): void {
    // SSR 环境下不执行
    if (import.meta.server) return;
    if (!this.initConfig?.autoConnect || !this.initConfig.baseURL) return;

    // 如果配置了 getAuthInfo，需要检查认证信息
    if (this.initConfig.getAuthInfo) {
      const authInfo = this.initConfig.getAuthInfo();
      if (!authInfo) {
        // 无认证信息，断开连接
        this.disconnect();
        return;
      }
      // 有认证信息，合并配置的 params 和认证信息
      const configParams = this.initConfig.params || {};
      const mergedParams = { ...configParams, ...authInfo };
      this.disconnect();
      this.connect({
        sseUrl: this.initConfig.baseURL,
        path: this.initConfig.path,
        params: Object.keys(mergedParams).length > 0 ? mergedParams : undefined,
      });
    } else {
      // 没有配置 getAuthInfo，使用配置的 params
      this.disconnect();
      this.connect({
        sseUrl: this.initConfig.baseURL,
        path: this.initConfig.path,
        params: this.initConfig.params,
      });
    }
  }

  // 连接 SSE
  public connect(config: SSEServiceConfig, forceConnect = false): void {
    // SSR 环境下不执行
    if (import.meta.server) return;

    if (this.sse && !forceConnect) {
      // SSE 已连接
      return;
    }

    // 如果强制重连，先关闭旧连接
    if (forceConnect && this.sse) {
      this.sse.destroy();
      this.sse = null;
    }

    // 构建 SSE URL
    const sseUrl = config.path
      ? buildApiUrl(config.sseUrl, config.path, config.params)
      : buildApiUrl(config.sseUrl, '', config.params);

    // 创建 SSE 连接
    this.sse = new SSEClient(sseUrl);

    // 设置消息处理
    this.sse.onMessage(msg => {
      // 通知所有订阅者
      this.subscribers.forEach(callback => {
        try {
          callback(msg);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('[SSEService] 订阅者处理消息时出错:', error);
        }
      });
    });

    // 设置错误处理
    this.sse.onError(error => {
      // 通知所有错误订阅者
      this.errorSubscribers.forEach(callback => {
        try {
          callback(error);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('[SSEService] 订阅者处理错误时出错:', err);
        }
      });
    });

    // 设置连接打开处理
    if (this.initConfig?.onOpen) {
      this.sse.onOpen(this.initConfig.onOpen);
    }

    // 设置连接关闭处理
    if (this.initConfig?.onClose) {
      this.sse.onClose(this.initConfig.onClose);
    }
  }

  // 订阅消息
  public subscribe(callback: SSEMessageCallback): () => void {
    if (!this.subscribers.includes(callback)) {
      this.subscribers.push(callback);
    }

    // 返回取消订阅函数
    return () => this.unsubscribe(callback);
  }

  // 取消订阅消息
  public unsubscribe(callback: SSEMessageCallback): void {
    this.subscribers = this.subscribers.filter(sub => sub !== callback);
  }

  // 订阅错误
  public subscribeError(callback: SSEErrorCallback): () => void {
    if (!this.errorSubscribers.includes(callback)) {
      this.errorSubscribers.push(callback);
    }

    // 返回取消订阅函数
    return () => this.unsubscribeError(callback);
  }

  // 取消订阅错误
  public unsubscribeError(callback: SSEErrorCallback): void {
    this.errorSubscribers = this.errorSubscribers.filter(sub => sub !== callback);
  }

  // 添加自定义事件监听器
  public addEventListener(event: string, callback: (event: MessageEvent) => void): void {
    if (this.sse) {
      this.sse.addEventListener(event, callback);
    }
  }

  // 移除自定义事件监听器
  public removeEventListener(event: string, callback: (event: MessageEvent) => void): void {
    if (this.sse) {
      this.sse.removeEventListener(event, callback);
    }
  }

  // 断开连接
  public disconnect(): void {
    if (this.sse) {
      this.sse.destroy();
      this.sse = null;
    }
  }

  // 销毁服务
  protected async onDestroy(): Promise<void> {
    if (this.sse) {
      this.sse.destroy();
      this.sse = null;
    }
    this.subscribers = [];
    this.errorSubscribers = [];
    // @ts-expect-error - 清理 initConfig 引用
    this.initConfig = null;
  }
}

// 全局 SSE 服务实例
let globalSSEService: SSEService | null = null;

// 初始化 SSE 服务
export const initSSEService = async (config?: SSEConfig): Promise<SSEService> => {
  // SSR 环境下不初始化 SSE 服务（SSE 服务仅在客户端使用）
  if (import.meta.server) {
    throw new Error('SSEService should not be initialized in SSR environment');
  }

  if (!globalSSEService) {
    globalSSEService = new SSEService(config);
    await globalSSEService.initialize();
  }
  return globalSSEService;
};

// 获取 SSE 服务实例
export const getSSEService = (): SSEService => {
  if (!globalSSEService) {
    throw new Error('SSEService not initialized. Call initSSEService first.');
  }
  return globalSSEService;
};

// 便捷的 Hook 函数（用于组件中订阅消息）
export function useSSE(callback: SSEMessageCallback) {
  // SSR 环境下返回空实现
  if (import.meta.server) {
    return {
      unsubscribe: () => {},
      subscribeError: () => () => {},
      addEventListener: () => {},
    };
  }

  const service = getSSEService();

  // 订阅消息
  const unsubscribe = service.subscribe(callback);

  return {
    // 取消订阅
    unsubscribe,
    // 订阅错误
    subscribeError: (errorCallback: SSEErrorCallback) => service.subscribeError(errorCallback),
    // 添加自定义事件监听器
    addEventListener: (event: string, eventCallback: (event: MessageEvent) => void) =>
      service.addEventListener(event, eventCallback),
  };
}
